import type { ColumnDef } from '@tanstack/react-table';
import {
  DataTableColumnHeader,
  dateRangeFilterFn,
  facetedFilterFn,
  numberRangeFilterFn,
} from '@folio/components';
import { formatDate, formatDateWithTime } from '@folio/lib';
import type { TFunction } from 'i18next';
import type { Field } from 'korint-sdk/react';
import { Text } from '@folio/components/ui/text';

export type FieldValuesRow = { values: Record<string, unknown> };

export const fieldLabel = (t: TFunction, field: Field) =>
  t([`fields.${field.name}.form.label`, `fields.${field.name}.label`, field.name]);

type FilterKind = 'faceted' | 'number' | 'date';

const filterKindForField = (field: Field): FilterKind => {
  if (field.type === 'number') {
    return 'number';
  }
  if (field.type === 'date' || field.type === 'datetime') {
    return 'date';
  }
  return 'faceted';
};

// Both formatters render Europe/Paris, so the midnight test has to read the same clock.
const parisTime = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const isParisMidnight = (date: Date) =>
  parisTime
    .formatToParts(date)
    .filter(({ type }) => type === 'hour' || type === 'minute' || type === 'second')
    .every(({ value }) => value === '00');

// Dates arrive as ISO strings; midnight means the time carries no information.
const displayValue = (field: Field, value: unknown) => {
  if (field.type !== 'date' && field.type !== 'datetime') {
    return String(value);
  }
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return field.type === 'date' || isParisMidnight(date)
    ? formatDate(date)
    : formatDateWithTime(date);
};

const filterFnByKind = {
  faceted: facetedFilterFn,
  number: numberRangeFilterFn,
  date: dateRangeFilterFn,
} as const;

/** One column per resolved field, read from `row.values`; the first one carries the row identity. */
export function fieldColumns<Row extends FieldValuesRow>({
  fields,
  labelForField,
}: {
  fields: Field[];
  labelForField: (field: Field) => string;
}): ColumnDef<Row>[] {
  return fields.map((field, fieldIndex) => ({
    id: field.name,
    accessorFn: row => row.values[field.name],
    // The identifier stays visible while the wider columns scroll under it.
    ...(fieldIndex === 0 && { meta: { sticky: 'left' as const } }),
    filterFn: filterFnByKind[filterKindForField(field)],
    header: ({ column }) => <DataTableColumnHeader column={column} title={labelForField(field)} />,
    cell: ({ getValue }) => {
      const value = getValue<unknown>();
      const isEmpty = value == null || value === '';
      return (
        <Text
          inline
          weight={fieldIndex === 0 ? 'medium' : undefined}
          tone={isEmpty ? 'muted' : undefined}
          className={fieldIndex === 0 ? 'tabular-nums' : undefined}
        >
          {isEmpty ? '—' : displayValue(field, value)}
        </Text>
      );
    },
  }));
}

/** Filter descriptors for `fieldColumns`, minus the identifier column, which is unique per row. */
export function fieldFilters({
  fields,
  labelForField,
}: {
  fields: Field[];
  labelForField: (field: Field) => string;
}) {
  return fields.slice(1).map(field => ({
    columnId: field.name,
    title: labelForField(field),
    type: filterKindForField(field),
  }));
}
